import React from 'react';
import styled from 'styled-components';
import { AuthContext } from 'contexts';
import { Spring } from 'react-spring/renderprops';
import { Alert } from '@brokerbay/design-components';
import {
  Text,
  RadioGroup,
  RadioButton,
  Card,
  Button,
  Checkbox,
} from 'components';
import {
  CreateListingCardProps,
  VisibilityFields,
  ListingFormState,
} from '../types';
import SmallPreview from './SmallPreview';

type VisibilityProps = CreateListingCardProps<VisibilityFields> & {
  initialListing: ListingFormState;
  editing?: boolean;
  submitting?: boolean;
  agreedToTerms: boolean;
  setAgreedToTerms: React.Dispatch<React.SetStateAction<boolean>>;
};

const Visibility: React.FC<VisibilityProps> = ({
  thumbnail,
  line1,
  line2,
  city,
  province,
  postal_code,
  list_price,
  visibility,
  pendingVisibility,
  initialListing,
  pageStatus,
  editing,
  submitting,
  status,
  agreedToTerms,
  setListing,
  setAgreedToTerms,
}) => {
  const { auth, hasFeature } = React.useContext(AuthContext);
  const showWarning = () => {
    if (initialListing.visibility === 'network') return false;
    return visibility === 'network' || pendingVisibility === 'network';
  };
  const editingPendingListing = visibility === 'pending' && editing;

  const showNetworkOption =
    (!editing || editingPendingListing) &&
    status !== 'MLS' &&
    hasFeature('NETWORK');

  const reset = () => {
    setListing(prev => ({
      ...prev,
      visibility: initialListing.visibility,
      pageStatus: initialListing.pageStatus,
    }));
  };

  React.useEffect(() => {
    if (!editing) {
      setListing(prev => ({
        ...prev,
        visibility: prev.status === 'Exclusive' ? 'network' : 'brokerage',
      }));
    }
    return () => {
      if (!editing) reset();
    };
  }, []);

  const visibilityCardTitle = () => {
    if (submitting) return null;
    if (editing) return 'Set Listing Visibility';
    return `Confirm ${auth?.isAdmin ? 'Publish' : 'Submission'} Details`;
  };
  const visibilityCardSubTitle = () => {
    if (editing) return null;
    const createListingSubTitleText = auth?.isAdmin
      ? `Please confirm your listing details are correct and select where this
    listing should be shared.`
      : `After you finish your listing, an admin from your brokerage will review and approve or deny this document.`;
    return (
      <Text block type="secondary">
        {createListingSubTitleText}
      </Text>
    );
  };

  const showAgreementText = () => (
      showNetworkOption &&
      (visibility === 'network' ||
        (visibility === 'pending' && pendingVisibility === 'network'))
    );

  return (
    <StyledCard
      type="primary"
      $hideBorder={submitting}
      title={visibilityCardTitle()}
      subtitle={visibilityCardSubTitle()}
    >
      <SmallPreview
        thumbnail={thumbnail}
        line1={line1}
        line2={line2}
        city={city}
        province={province}
        postal_code={postal_code}
        list_price={list_price}
      />
      {(!editing || visibility !== 'network') && (
        <StyledRadioGroup
          label={
            editing
              ? 'Who should see this listing?'
              : 'Where should this listing be shared?'
          }
          buttonStyle="solid"
          value={editingPendingListing ? pendingVisibility : visibility}
          onChange={({ target: { value } }) =>
            setListing(prev => ({
              ...prev,
              [editingPendingListing ? 'pendingVisibility' : 'visibility']:
                value,
            }))
          }
        >
          {auth.isAdmin && !editingPendingListing && (
            <RadioButton value="private" data-cy="visibility-private">
              {!editing ? 'Keep as Draft' : 'Draft'}
            </RadioButton>
          )}
          <RadioButton value="brokerage" data-cy="visibility-brokerage">
            {status === 'MLS' ? 'Everyone' : 'My Brokerage'}
          </RadioButton>
          {showNetworkOption && (
            <RadioButton value="network" data-cy="visibility-network">
              Exclusive Network
            </RadioButton>
          )}
        </StyledRadioGroup>
      )}
      <Spring
        to={{
          maxHeight: showWarning() ? 200 : 0,
          opacity: showWarning() ? 1 : 0,
          marginTop: showWarning() ? 25 : 0,
          overflow: 'hidden',
        }}
      >
        {props => (
          <div style={props}>
            <Alert
              message="Warning"
              description="You acknowledge that you are about to share your listing with the entire BrokerBay community of brokerages."
              type="warning"
              showIcon
            />
          </div>
        )}
      </Spring>
      {(visibility === 'brokerage' ||
        (visibility === 'pending' && pendingVisibility === 'brokerage')) && (
        <StyledRadioGroup
          label="Would you like to notify your brokerage?"
          buttonStyle="solid"
          value={pageStatus}
          onChange={({ target: { value } }) =>
            setListing(prev => ({ ...prev, pageStatus: value }))
          }
        >
          <RadioButton value="new" data-cy="brokerage-new">
            Yes
          </RadioButton>
          <RadioButton value="paged" data-cy="brokerage-paged">
            No
          </RadioButton>
        </StyledRadioGroup>
      )}
      {showAgreementText() && (
        <StyledTextDiv>
          <Text>
            By Submitting this listing for Review, you agree that you have read
            and accept the Exclusive Network terms, privacy policy and
            acceptable use policy which can be found in the link below.
          </Text>

          <div>
            <Button
              href="https://www.brokerbay.com/terms-of-service"
              target="_blank"
              type="link"
              data-cy="terms-of-use-link"
            >
              Acceptable Use Policy / Terms of Service
            </Button>
          </div>

          <Checkbox
            onChange={e => {
              setAgreedToTerms(e.target.checked);
            }}
            checked={agreedToTerms}
            data-cy="checkbox-terms"
          >
            I have read and accept the terms
          </Checkbox>
        </StyledTextDiv>
      )}
    </StyledCard>
  );
};

const StyledCard = styled(Card)<{ $hideBorder?: boolean }>`
  width: 100%;
  max-width: 600px;
  margin: auto;
  ${({ $hideBorder }) => $hideBorder && 'border: none'};
`;

const StyledRadioGroup = styled(RadioGroup)`
  margin-top: 25px;
`;

const StyledTextDiv = styled.div`
  > * {
    margin-top: 25px;
  }
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export default React.memo(Visibility);
